import React, {
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { makeStyles, type Theme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";

import Dialog from "src/components/Dialog";
import LoadableButton from "src/components/LoadableButton";
import ConfirmDialog from "src/components/ConfirmDialog";
import VendorField from "src/components/VendorField";
import { isPdfReceiptFilename } from "src/components/ReceiptDialog";
import { setActiveDialog } from "src/store/adminHtml/actions";
import type { AppThunkDispatch } from "src/store/types";
import { activeDialogPropsSelector } from "src/store/adminHtml/selector";
import { splitGroupSelector } from "src/store/transaction/selector";
import type {
  Transaction,
  TransactionFormError,
} from "src/interfaces/transaction.interface";
import type { EditSplitProps } from "src/interfaces/adminHtmlState.interface";
import {
  loadSplitTransactionFormModel,
  type SplitCommonFields,
} from "src/apiService/modules/transactions";
import {
  deleteSplitGroupOptimistic,
  saveSplitGroupReceipt,
  unsplitTransactionOptimistic,
  upsertSplitTransactionOptimistic,
} from "src/store/transaction/actions";
import { useUploadedFile } from "src/utils/useUploadedFile";
import { useRecordEvent } from "src/hooks/useRecordEvent";

import { DefaultForm } from "src/pages/TransactionDetails/Expenses/EditDialog/DefaultForm";
import { Row } from "src/pages/TransactionDetails/Expenses/EditDialog/Row";
import {
  SplitSection,
  type SplitRowState,
} from "src/pages/TransactionDetails/Expenses/EditDialog/SplitSection";
import { TransactionTypeField } from "src/pages/TransactionDetails/Expenses/EditDialog/TransactionTypeField";
import { ReceiptControl } from "src/pages/TransactionDetails/Expenses/EditDialog/ReceiptControl";
import {
  isValidDate,
  signedAmount,
} from "src/pages/TransactionDetails/Expenses/EditDialog/utils";

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    position: "relative",
  },
  options: {
    position: "absolute",
    top: 0,
    right: 24,
    bottom: 0,
    display: "flex",
    alignItems: "center",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    "& > * + *": {
      marginTop: theme.spacing(1),
    },
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
}));

/** Normalized view-model the split form is initialized from. */
interface SplitContentModel {
  split_group_id: string;
  base: Partial<Transaction>;
  rows: SplitRowState[];
  total: number;
}

function genKey(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function toSplitRowState(row: {
  id?: string;
  amount?: number;
  gl_account_id?: string;
  account?: string;
  description?: string;
}): SplitRowState {
  return {
    key: row.id || genKey(),
    id: row.id,
    amount: typeof row.amount === "number" ? row.amount : 0,
    gl_account_id: row.gl_account_id,
    account: row.account,
    description: row.description,
  };
}

/**
 * Fallback used when the split group isn't in Redux yet (e.g. a deep link or a
 * report drill-down opened before transactions finished loading). Fetches the
 * clicked doc and its siblings, then maps them onto the form's view-model.
 */
async function fetchSplitModel(
  id: string
): Promise<SplitContentModel | undefined> {
  const model = await loadSplitTransactionFormModel(id);
  if (!model) return undefined;
  return {
    split_group_id: model.split.split_group_id || "",
    base: model.transaction as Partial<Transaction>,
    total: model.split.total,
    rows: model.split.rows.map(toSplitRowState),
  };
}

function Content({
  model,
  requestId,
  onClose,
  holdModelRef,
}: {
  model: SplitContentModel;
  requestId?: string;
  onClose: (updated?: boolean) => void;
  /** Set while delete or unsplit is in flight so the confirm dialog stays up after Redux drops the group. */
  holdModelRef: MutableRefObject<boolean>;
}) {
  const dispatch = useDispatch<AppThunkDispatch>();
  const recordEvent = useRecordEvent();
  const classes = useStyles();
  const dateRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const openedAsMoneyIn = Object.is(model.total, -0) || model.total < 0;
  const [values, setValues] = useState<Partial<Transaction>>({
    ...model.base,
    // Keep the stored sign here so Money In starts on for a negative group.
    // The lines show the amounts as they were entered under that toggle.
    amount: model.total,
  });
  const [splitTotal, setSplitTotal] = useState<number | "">(
    signedAmount(model.total, openedAsMoneyIn)
  );
  const [splitRows, setSplitRows] = useState<SplitRowState[]>(() =>
    model.rows.map((row) => ({
      ...row,
      amount:
        typeof row.amount === "number"
          ? signedAmount(row.amount, openedAsMoneyIn)
          : row.amount,
    }))
  );
  const [errors, setErrors] = useState<TransactionFormError>({});
  const receipt = useUploadedFile(values.receipt_filename, {
    label: "receipt",
  });
  const receiptIsPdf =
    !!values.receipt_filename && isPdfReceiptFilename(values.receipt_filename);
  const [dialog, setDialog] = useState<
    undefined | "receipt" | "confirm-delete" | "confirm-unsplit"
  >(undefined);

  const isMoneyIn = Object.is(values.amount, -0) || (values.amount || 0) < 0;

  const splitSum = splitRows.reduce(
    (acc, r) => acc + (typeof r.amount === "number" ? r.amount : 0),
    0
  );
  const splitTotalNum = typeof splitTotal === "number" ? splitTotal : 0;
  const splitBalanced = Math.abs(splitTotalNum - splitSum) < 0.005;
  const splitRowsValid =
    splitRows.length > 0 &&
    splitRows.every(
      (r) =>
        r.gl_account_id &&
        r.account &&
        typeof r.amount === "number" &&
        r.amount !== 0
    );
  const splitValid =
    typeof splitTotal === "number" &&
    splitTotal !== 0 &&
    splitRowsValid &&
    splitBalanced;

  const handleChange =
    (prop: keyof Transaction) => (event: { target: { value?: any } }) => {
      const value = event.target.value;
      setValues((v) => ({ ...v, [prop]: value }));
    };

  const validateForm = (): boolean => {
    let hasError = false;
    const newErrors: TransactionFormError = { ...errors };

    if (!splitValid) {
      hasError = true;
      newErrors.amount =
        splitTotal === "" || splitTotal === 0
          ? "Total amount is required"
          : !splitBalanced
          ? "Split amounts must equal the total"
          : "All split rows require an amount and GL account";
    } else {
      newErrors.amount = undefined;
    }

    if (!isValidDate(values.date)) {
      hasError = true;
      newErrors.date = "Invalid date format";
    } else {
      newErrors.date = undefined;
    }

    if (!values.integration_name) {
      hasError = true;
      newErrors.integration_name = "Required field!";
    } else {
      newErrors.integration_name = undefined;
    }

    setErrors(newErrors);
    return !hasError;
  };

  const buildCommon = (): SplitCommonFields => ({
    ...(values.date !== undefined && { date: values.date }),
    ...(values.description !== undefined && {
      description: values.description,
    }),
    ...("vendor" in values ? { vendor: values.vendor } : {}),
    ...(values.integration_name !== undefined && {
      integration_name: values.integration_name,
    }),
    ...(values.bank_account_id !== undefined && {
      bank_account_id: values.bank_account_id,
    }),
    ...(values.upload !== undefined && { upload: values.upload }),
  });

  const handleSave = async (event?: any) => {
    if (event?.preventDefault) event.preventDefault();
    if (!validateForm()) {
      toast.error("Some required information is missing or incomplete");
      return;
    }
    setLoading(true);
    try {
      const rows = splitRows.map((r) => ({
        ...(r.id ? { id: r.id } : {}),
        amount: signedAmount(
          typeof r.amount === "number" ? r.amount : 0,
          isMoneyIn
        ),
        gl_account_id: r.gl_account_id!,
        account: r.account!,
        description: r.description ?? "",
      }));
      await dispatch(
        upsertSplitTransactionOptimistic({
          split_group_id: model.split_group_id,
          common: buildCommon(),
          rows,
        })
      );
      toast.success("Split transaction has been updated successfully");
      onClose(true);
    } catch (e) {
      toast.error(`There was an error: ${(e as Error).toString()}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    // The optimistic delete removes the group from Redux before the request
    // returns. Hold the form so this confirm dialog can show its spinner
    // until the delete finishes.
    holdModelRef.current = true;
    try {
      await dispatch(deleteSplitGroupOptimistic(model.split_group_id));
      toast.success("Split transaction has been deleted successfully");
      onClose(true);
    } catch (e) {
      holdModelRef.current = false;
      throw e;
    }
  };

  const handleUnsplit = async () => {
    // Collapse the group into a single transaction using the primary row's
    // category. Only carry fields that are actually set: Firestore's `set()`
    // rejects `undefined`. The server writes the new row and deletes the
    // group together, and keeps the stored receipt. The optimistic update
    // drops the group from Redux immediately, so hold the form until the
    // request finishes and this confirm button can keep spinning.
    holdModelRef.current = true;
    setLoading(true);
    try {
      const primary = splitRows[0];
      const single: Partial<Transaction> = {
        amount: signedAmount(splitTotalNum, isMoneyIn),
        ...(values.date !== undefined && { date: values.date }),
        ...(values.description !== undefined && {
          description: values.description,
        }),
        ...("vendor" in values ? { vendor: values.vendor } : {}),
        ...(values.integration_name !== undefined && {
          integration_name: values.integration_name,
        }),
        ...(values.bank_account_id !== undefined && {
          bank_account_id: values.bank_account_id,
        }),
        ...(values.receipt_filename && {
          receipt_filename: values.receipt_filename,
        }),
        ...(primary?.gl_account_id !== undefined && {
          gl_account_id: primary.gl_account_id,
        }),
        ...(primary?.account !== undefined && { account: primary.account }),
      };
      const newId = await dispatch(
        unsplitTransactionOptimistic(model.split_group_id, single)
      );
      toast.success("Split transaction converted to a single transaction");
      // Hand straight off to the regular edit dialog for the new single row so it
      // feels like a transition rather than a close/reopen. The optimistic
      // unsplit patch has already put that row in Redux, so the edit dialog
      // opens it without a fetch flash.
      dispatch(setActiveDialog("edit_transaction", { id: newId, requestId }));
    } catch (e) {
      holdModelRef.current = false;
      toast.error(`There was an error: ${(e as Error).toString()}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ConfirmDialog
        open={dialog === "confirm-delete"}
        title="Delete split transaction?"
        text="This will delete every line in the split group. This cannot be undone."
        onCancel={() => setDialog(undefined)}
        onConfirm={handleDeleteGroup}
      />
      <ConfirmDialog
        open={dialog === "confirm-unsplit"}
        title="Convert to a single transaction?"
        text="This will merge every line into one transaction using the first line's general ledger account. The other lines' categories will be lost."
        onCancel={() => setDialog(undefined)}
        onConfirm={handleUnsplit}
      />

      <form noValidate autoComplete="off" onSubmit={handleSave}>
        <DialogTitle className={classes.title}>
          Edit Split Transaction
          <span className={classes.options}>
            <IconButton
              onClick={() => setDialog("confirm-delete")}
              disabled={loading}
              className={classes.deleteButton}
            >
              <DeleteOutlineOutlinedIcon />
            </IconButton>
          </span>
        </DialogTitle>
        <DialogContent className={classes.content}>
          <TransactionTypeField
            isMoneyIn={isMoneyIn}
            loading={loading}
            isUpdate
            onDirectionChange={(nextMoneyIn) => {
              // The lines stay as entered. Money Out saves that sign. Money In
              // saves the opposite sign.
              setValues((v) => ({
                ...v,
                amount: signedAmount(Math.abs(v?.amount || 0), nextMoneyIn),
              }));
            }}
            onJournalEntry={() => undefined}
          />
          <Row text="Where the purchase was made.">
            <VendorField
              margin="dense"
              disabled={loading}
              value={values.vendor}
              onChange={(vendor) => {
                setValues((v) => ({ ...v, vendor }));
              }}
            />
          </Row>
          <DefaultForm
            values={values}
            errors={errors}
            handleChange={handleChange}
            loading={loading}
            setValues={setValues}
            dateRef={dateRef}
            hideAmount
            hideDescription
          />
          <SplitSection
            rows={splitRows}
            onChangeRows={setSplitRows}
            total={splitTotal}
            onChangeTotal={setSplitTotal}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <ReceiptControl
            receiptUrl={receipt.url}
            hasReceipt={receipt.hasFile}
            receiptLoading={receipt.loading}
            receiptIsPdf={receiptIsPdf}
            loading={loading}
            open={dialog === "receipt"}
            onOpen={() => setDialog("receipt")}
            onClose={() => setDialog(undefined)}
            onConfirm={async (file?: File) => {
              if (!file) {
                setDialog(undefined);
                return;
              }
              recordEvent({
                type: "feature-adoption",
                feature: "receipt-upload",
              });
              try {
                const filename = await dispatch(
                  saveSplitGroupReceipt(
                    model.split_group_id,
                    model.base.id ?? model.split_group_id,
                    file
                  )
                );
                setValues((v) => ({
                  ...v,
                  receipt_filename: filename ?? undefined,
                }));
                setDialog(undefined);
                toast.success("Receipt attached.");
              } catch (e) {
                toast.error("The receipt couldn't be saved. Please try again.");
              }
            }}
            onDelete={async () => {
              try {
                await dispatch(
                  saveSplitGroupReceipt(
                    model.split_group_id,
                    model.base.id ?? model.split_group_id
                  )
                );
                receipt.clear();
                setValues(({ receipt_filename, ...v }) => v);
                toast.success("Receipt deleted.");
              } catch (e) {
                // Close the popup so it doesn't show the receipt as gone.
                setDialog(undefined);
                toast.error(
                  "The receipt couldn't be deleted. Please try again."
                );
              }
            }}
          />
          <Box flex={1} />
          <Button
            onClick={() => setDialog("confirm-unsplit")}
            color="primary"
            variant="outlined"
            disabled={loading}
          >
            Unsplit
          </Button>
          <LoadableButton
            color="primary"
            variant="contained"
            loading={loading}
            type="submit"
            disabled={!splitValid}
          >
            Update
          </LoadableButton>
        </DialogActions>
      </form>
    </>
  );
}

function Wrapper({ onClose }: { onClose: (updated?: boolean) => void }) {
  const activeDialogProps = useSelector(
    activeDialogPropsSelector
  ) as EditSplitProps;
  const [ids] = useState(() => ({
    split_group_id: activeDialogProps?.split_group_id,
    id: activeDialogProps?.id,
    requestId: activeDialogProps?.requestId,
  }));
  const fromRedux = useSelector((state) =>
    splitGroupSelector(state, ids.split_group_id, ids.id)
  );
  const [fetched, setFetched] = useState<SplitContentModel | undefined>(
    undefined
  );
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const modelRef = useRef<SplitContentModel | undefined>(undefined);
  const holdModelRef = useRef(false);

  useEffect(() => {
    if (fromRedux || holdModelRef.current) return;
    let cancelled = false;
    (async () => {
      if (!ids.id) {
        onCloseRef.current(false);
        return;
      }
      try {
        const model = await fetchSplitModel(ids.id);
        if (cancelled) return;
        if (!model) onCloseRef.current(false);
        else setFetched(model);
      } catch (e) {
        if (!cancelled) onCloseRef.current(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fromRedux, ids]);

  const model: SplitContentModel | undefined = fromRedux
    ? {
        split_group_id: fromRedux.split_group_id,
        base: fromRedux.base,
        total: fromRedux.total,
        rows: fromRedux.rows
          .map(toSplitRowState)
          .sort((a, b) =>
            a.id === fromRedux.base.id ? -1 : b.id === fromRedux.base.id ? 1 : 0
          ),
      }
    : fetched;

  if (model) modelRef.current = model;
  const displayed =
    model ?? (holdModelRef.current ? modelRef.current : undefined);
  if (!displayed) return null;
  return (
    <Content
      model={displayed}
      requestId={ids.requestId}
      onClose={onClose}
      holdModelRef={holdModelRef}
    />
  );
}

function EditSplitDialog({ open }: { open: boolean }) {
  const dispatch = useDispatch();
  const activeDialogProps = useSelector(
    activeDialogPropsSelector
  ) as EditSplitProps;
  const handleClose = (updated?: boolean) =>
    dispatch(
      setActiveDialog("", {
        requestId: activeDialogProps?.requestId,
        updated: !!updated,
      })
    );

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(false)}
      scroll="body"
      fullWidth
      maxWidth="sm"
      disableEnforceFocus
    >
      {open ? <Wrapper onClose={handleClose} /> : null}
    </Dialog>
  );
}

export default EditSplitDialog;
